import { text } from "../utils/i18n";
import { BooleanOrAny } from "./BooleanOrAny";
import { OfferStatus, translateOfferStatus } from "./Offer";
import { OfferReviewDecision } from "./OfferReview";
import { OfferTemplate } from "./OfferTemplate";
import Sort from "./Sort";

export enum OfferDateType {
  Created = 'created',
  Signed = 'signed'
}

/**
 * Filters for offers.
 */
export default class OfferFilters {
  readonly ids: string[] = [];
  readonly query?: string;
  readonly from?: Date;
  readonly to?: Date;
  readonly sumFrom?: number;
  readonly sumTo?: number;
  readonly userIds: string[] = [];
  readonly teamIds: string[] = [];

  /**
   * Whether to exclude `userIds`.
   */
  readonly excludeUserIds?: boolean;
  readonly templateIds: string[] = [];
  readonly statuses: OfferStatus[] = [];
  readonly statusUpdatedAfter?: Date | RelativeDate;
  readonly statusUpdatedBefore?: Date | RelativeDate;
  readonly sort = new Sort({ asc: false, field: 'date' });
  readonly offset: number = 0;
  readonly count: number = 50;
  readonly isTest: BooleanOrAny = BooleanOrAny.False;
  readonly projectIds?: string[];
  /**
   * Acts like `search`, but can expand the right scope, for viewing
   * a contact's associated offers.
   */
  readonly phoneNumbers: string[] = [];
  readonly anyReviewDecision: OfferReviewDecision[] = [];
  readonly allReviewDecisions: OfferReviewDecision[] = [];
  readonly notAnyReviewDecision: OfferReviewDecision[] = [];
  readonly notAllReviewDecisions: OfferReviewDecision[] = [];
  readonly dateType: OfferDateType = OfferDateType.Created;

  constructor(deriveFrom?: Partial<OfferFilters>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }
  }

  toQuery() {
    return {
      s: this.query,
      statuses: this.statuses,
      userIds: this.userIds,
      teamIds: this.teamIds,
      from: this.from,
      to: this.to,
      count: this.count,
      offset: this.offset,
      templateIds: this.templateIds,
      sumFrom: this.sumFrom,
      sumTo: this.sumTo,
      ids: this.ids,
      isTest: this.isTest,
      projectIds: this.projectIds,
      phoneNumbers: this.phoneNumbers,
      anyReviewDecision: this.anyReviewDecision,
      notAnyReviewDecision: this.notAnyReviewDecision,
      allReviewDecisions: this.allReviewDecisions,
      notAllReviewDecisions: this.notAllReviewDecisions,
      dateType: this.dateType,
      ...this.sort.toQuery()
    }
  }
}

const allStatuses = Object.values(OfferStatus);

export function listOfferFilterDescriptions(filters: Partial<OfferFilters>, offerTemplates: OfferTemplate[]): string[] {
  const descriptions: string[] = [];

  if (filters.statuses?.length) {
    const excludedStatuses = allStatuses.filter(status => !filters.statuses?.includes(status));

    if (excludedStatuses.length === 1) {
      //  Quotes because: Ej "Ej skickat"
      descriptions.push(
        text`ej ${`"${translateOfferStatus(excludedStatuses[0])}"`}`
      );

    } else if (filters.statuses?.length > 2) {
      descriptions.push(text`${filters.statuses.length} statusar`);

    } else {
      descriptions.push(
        filters.statuses.map(status => translateOfferStatus(status)).join(', ')
      );
    }
  }

  const templates = filters.templateIds?.length ?
    offerTemplates.filter(template => filters.templateIds.includes(template.id)) : [];

  if (templates.length) {
    descriptions.push(templates.map(template => template.title).join('/'));
  }

  return descriptions;
}

/**
 * @returns Whether any non-default filter is present, i.e. if the user has entered a filter. 
 */
export function isOfferFiltersEmpty(filters: Partial<OfferFilters> | null | undefined): boolean {
  if (!filters) {
    return null;  //  Must return null in VariableList.
  }

  return !(
    filters.query || filters.from || filters.to || filters.templateIds?.length ||
    filters.userIds?.length || filters.teamIds?.length || filters.statuses?.length || filters.sumFrom ||
    filters.sumTo || filters.ids?.length || (filters.isTest && filters.isTest !== BooleanOrAny.False)
  );
}